/**
 * Picking Stats Widget
 *
 * Displays picking statistics including active pickers,
 * lines picked, efficiency, and performance metrics.
 * Connected to live Dashboard API with mock fallback.
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import React from 'react';
import { Users, Package, Zap, Timer, TrendingUp } from 'lucide-react';
import { WidgetInstance, PickingStats } from '../../types';
import { getPickingStats } from '../../mockDataService';
import { fetchPickingStats } from '../../../../../../services/dashboardService';
import { useWidgetData } from '../../hooks/useWidgetData';
import WidgetWrapper from '../WidgetWrapper';
import { WidgetSkeleton } from '../shared';

interface PickingStatsWidgetProps {
  instance: WidgetInstance;
}

const PickingStatsWidget: React.FC<PickingStatsWidgetProps> = ({ instance }) => {
  const { data, isLoading, error, refresh, isFirstLoad, isLive } = useWidgetData<PickingStats>({
    widgetId: 'picking-stats',
    instance,
    fetchFn: async () => {
      try {
        return await fetchPickingStats();
      } catch {
        return getPickingStats();
      }
    },
  });

  if (isFirstLoad) {
    return (
      <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={true}>
        <WidgetSkeleton variant="stat" count={5} />
      </WidgetWrapper>
    );
  }

  const getEfficiencyColor = (efficiency: number) => {
    if (efficiency >= 95) return 'text-green-400';
    if (efficiency >= 80) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={isLoading} error={error?.message} isLive={isLive}>
      {data && (
        <div className="h-full flex flex-col gap-3">
          {/* Top stats row */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-lg p-3 text-center">
              <Users className="w-5 h-5 text-blue-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-white">{data.activePickers}</div>
              <div className="text-xs text-gray-400">Aktive plukkere</div>
            </div>
            <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-lg p-3 text-center">
              <Package className="w-5 h-5 text-green-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-white">{data.ordersInPicking}</div>
              <div className="text-xs text-gray-400">Ordrer i plukk</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-lg p-3 text-center">
              <Zap className={`w-5 h-5 mx-auto mb-1 ${getEfficiencyColor(data.efficiency)}`} />
              <div className={`text-2xl font-bold ${getEfficiencyColor(data.efficiency)}`}>
                {data.efficiency}%
              </div>
              <div className="text-xs text-gray-400">Effektivitet</div>
            </div>
          </div>

          {/* Bottom stats */}
          <div className="flex-1 grid grid-cols-2 gap-2">
            <div className="bg-gray-700/30 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-400">Linjer i dag</span>
                <TrendingUp className="w-4 h-4 text-green-400" />
              </div>
              <div className="text-xl font-bold text-white">{data.linesPickedToday.toLocaleString()}</div>
            </div>
            <div className="bg-gray-700/30 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-400">Linjer/time</span>
                <Timer className="w-4 h-4 text-blue-400" />
              </div>
              <div className="text-xl font-bold text-white">{data.pickRate}</div>
            </div>
          </div>

          {/* Progress bar for efficiency */}
          <div>
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Plukkeffektivitet</span>
              <span>{data.efficiency}%</span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 rounded-full ${
                  data.efficiency >= 95
                    ? 'bg-gradient-to-r from-green-500 to-green-400'
                    : data.efficiency >= 80
                    ? 'bg-gradient-to-r from-yellow-500 to-yellow-400'
                    : 'bg-gradient-to-r from-red-500 to-red-400'
                }`}
                style={{ width: `${Math.min(100, data.efficiency)}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </WidgetWrapper>
  );
};

export default PickingStatsWidget;
